// Knowledge Atlas API client.
//
// Talks to the fixed `/ops/knowledge-atlas/*` JSON contract (camelCase payloads).
// Follows the repo fetch convention (see app/frontend/api/topics.ts):
// X-Requested-With, X-CSRF-Token from the meta tag, same-origin credentials,
// and no-store caching for freshness.

// ---------------------------------------------------------------------------
// Contract types
// ---------------------------------------------------------------------------

/** A municipality available in the page-level picker (passed as an Inertia prop). */
export interface AtlasMunicipality {
  id: number;
  name: string;
  documentCount: number;
  /**
   * True when a warmed atlas exists for the city's CURRENT corpus version (its persisted build
   * key still matches the freshly computed one). The picker only lets you select ready cities, so
   * a selection always hits a warm cache instead of triggering a slow inline build. Unready cities
   * are warmed in the background (the scheduled warm pass) and become selectable once built.
   */
  ready: boolean;
}

/** A knowledge district (planning zone) on the map. */
export interface AtlasDistrict {
  id: number;
  label: string;
  documentCount: number;
  /** Hex color (e.g. "#5B7C99") used as the district's zoning tint. */
  color: string;
  centerX: number;
  centerY: number;
  radius: number;
  /**
   * Closed polygon ring (zoning parcel) in the same x/y coordinate space as
   * documents. Rendered as a pale filled zone with a crisp border. May be
   * absent on older payloads — the renderer degrades to a radial center/radius
   * disc when missing.
   */
  boundary?: [number, number][];
  /**
   * The themes this district most strongly shares with OTHER districts — the
   * topics that "bridge" it to the rest of the municipality (e.g. "detention",
   * "setbacks"). Surfaced when a district is hovered/selected. May be absent on
   * older payloads; the UI simply omits the bridge-themes line when missing.
   */
  bridgeThemes?: string[];
  /**
   * A short, written description of the district (one sentence), produced by the
   * backend's prose pass. When present, the UI prefers it over the raw
   * `bridgeThemes` term list (e.g. in the district hover chip). Optional: absent
   * on older payloads, in which case the UI falls back to `bridgeThemes`.
   */
  summary?: string;
}

/** A document landmark with a fixed, precomputed position. */
export interface AtlasDocument {
  id: number;
  title: string;
  docType: string;
  chunkCount: number;
  relationshipCount: number;
  lastUpdated: string;
  districtId: number;
  x: number;
  y: number;
  /** Connection count, used to scale landmark size / brightness. */
  degree: number;
}

/** A relationship "road" between two documents (by document id). */
export interface AtlasRelationship {
  source: number;
  target: number;
  weight: number;
  /**
   * The themes the two connected documents share — i.e. WHY they are linked
   * (e.g. ["stormwater", "detention"]). Powers the hover "why" card and the
   * on-map edge theme-labels. May be absent on older payloads; the UI degrades
   * to showing the connection without an explanation.
   */
  sharedTerms?: string[];
  /**
   * A written sentence explaining WHY the two documents are connected, produced
   * by the backend's prose pass for strong edges (clearer than a bare term
   * list). When present, the hover "why" card and the IntelligencePanel prefer
   * it over `sharedTerms`. Optional: absent on older payloads or weak edges, in
   * which case the UI falls back to `sharedTerms`.
   */
  rationale?: string;
}

/**
 * A cross-district connection: the aggregate link between two knowledge
 * districts, with its strength and the themes that bridge them. Used for the
 * quiet connections legend and the most-significant on-map edge labels. May be
 * absent on older payloads; consumers degrade to inferring cross-district links
 * from the highest-weight `relationships` with non-empty `sharedTerms`.
 */
export interface AtlasConnection {
  sourceDistrictId: number;
  targetDistrictId: number;
  strength: number;
  sharedTerms: string[];
  /**
   * A written sentence describing the cross-district connection, from the
   * backend's prose pass. When present, the connections legend prefers it over
   * the `sharedTerms` list on row hover. Optional: absent on older payloads, in
   * which case the legend falls back to `sharedTerms`.
   */
  rationale?: string;
}

export interface AtlasMeta {
  documentsWithoutEmbeddings: number;
}

export interface AtlasData {
  municipality: {
    id: number;
    name: string;
    documentCount: number;
    chunkCount: number;
  };
  districts: AtlasDistrict[];
  documents: AtlasDocument[];
  relationships: AtlasRelationship[];
  /**
   * Aggregate cross-district connections (the structural "trade routes" between
   * knowledge districts). Optional: absent on older payloads, in which case the
   * UI derives the strongest cross-district links from `relationships`.
   */
  connections?: AtlasConnection[];
  meta: AtlasMeta;
  /**
   * The "downtown core" document: the most-connected landmark, rendered as the
   * map's civic anchor (a bold marker inside a concentric city-center ring,
   * always-labeled). `null` when the corpus has no clear anchor; the renderer
   * then shows no special core.
   */
  anchorDocumentId?: number | null;
  /**
   * Knowledge-coverage GAPS: what the corpus is MISSING or weak on, surfaced so
   * the map shows not just what's mapped but what isn't. Each gap is one of:
   *   - 'isolated'      a document with no (or almost no) connections — knowledge
   *                     that sits disconnected from the rest of the municipality.
   *   - 'underconnected' a document/district that is more weakly linked than its
   *                     peers (present, but thinly wired in).
   *   - 'thin'          a district with shallow coverage (few documents/chunks for
   *                     a topic that should have more).
   * `severity` is 0..1 (higher = more notable). `documentId` / `districtId` point
   * at the subject so the UI can fly to / select it. Optional: absent on older
   * payloads, in which case the coverage panel and on-map flags simply don't show.
   */
  gaps?: AtlasGap[];
}

/** A single knowledge-coverage gap (see AtlasData.gaps). */
export interface AtlasGap {
  kind: 'isolated' | 'underconnected' | 'thin';
  title: string;
  detail: string;
  documentId?: number;
  districtId?: number;
  /** 0..1; higher = more notable. Used to sort within a group. */
  severity: number;
}

export interface ConnectedDocument {
  id: number;
  title: string;
  weight: number;
  /**
   * The themes this connected document shares with the focused one. The
   * document-intelligence endpoint may add this later; the panel shows it when
   * present and omits it otherwise. (Hover-card sharing reads from the atlas
   * `relationships[].sharedTerms` instead, which is available eagerly.)
   */
  sharedTerms?: string[];
  /**
   * A written sentence explaining the connection (backend prose pass). When the
   * intelligence endpoint supplies it, the panel prefers it over `sharedTerms`
   * (clearer). Optional: absent on older payloads, in which case the panel falls
   * back to `sharedTerms`.
   */
  rationale?: string;
}

/** Intelligence-panel payload for a single document. */
export interface DocumentIntelligence {
  documentId: number;
  title: string;
  docType: string;
  chunkCount: number;
  relationshipCount: number;
  connectedDocuments: ConnectedDocument[];
  connectedTopics: string[];
  coverage: string;
  recentActivity: string;
}

export interface SearchResultDocument {
  id: number;
  title: string;
  score: number;
  /**
   * The query terms this document matched on (e.g. ["detention", "easement"]),
   * so the results panel can explain WHY it surfaced ("matched on: …"). Optional:
   * absent on older payloads; the UI simply omits the matched-terms line.
   */
  matchedTerms?: string[];
}

export interface SearchResultDistrict {
  id: number;
  label: string;
  score: number;
  /** The query terms this district matched on. See SearchResultDocument. */
  matchedTerms?: string[];
}

export interface AtlasSearchResults {
  /**
   * A one/two-sentence natural-language answer to the query, synthesized by the
   * backend ("The atlas covers privacy fences across the Code of Ordinances …").
   * Shown as the headline atop the results panel. Optional: absent on older
   * payloads or when the backend declines to answer; the UI just shows matches.
   */
  answer?: string | null;
  documents: SearchResultDocument[];
  districts: SearchResultDistrict[];
}

// ---------------------------------------------------------------------------
// Fetch helper (mirrors the repo convention in api/topics.ts)
// ---------------------------------------------------------------------------

function atlasHeaders(): HeadersInit {
  return {
    'X-Requested-With': 'XMLHttpRequest',
    'X-CSRF-Token':
      document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
  };
}

async function getJson<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    headers: atlasHeaders(),
    cache: 'no-store',
    credentials: 'same-origin',
    referrerPolicy: 'same-origin',
  });

  if (!response.ok) {
    throw new Error(`Atlas request failed (${response.status}): ${response.statusText}`);
  }

  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/** Options for {@link getAtlas}. */
export interface GetAtlasOptions {
  /** Called once when the server reports the atlas is building (first 202), so the UI can show a
   *  "building" state. Not called when the atlas is served straight from cache. */
  onBuilding?: () => void;
  /** Aborts the request (and any polling) when the caller unmounts / switches municipality. */
  signal?: AbortSignal;
}

// The data endpoint returns 202 while the atlas warms in the background. Poll until it's ready.
const ATLAS_BUILD_POLL_MS = 2500;
const ATLAS_BUILD_TIMEOUT_MS = 180_000;

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const cleanup = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    };
    const onAbort = () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort);
  });
}

export async function getAtlas(
  municipalityId: number,
  options: GetAtlasOptions = {}
): Promise<AtlasData> {
  const params = new URLSearchParams({ municipality_id: String(municipalityId) });
  const url = `/ops/knowledge-atlas/data?${params.toString()}`;
  const deadline = Date.now() + ATLAS_BUILD_TIMEOUT_MS;
  let notifiedBuilding = false;

  for (;;) {
    const response = await fetch(url, {
      headers: atlasHeaders(),
      cache: 'no-store',
      credentials: 'same-origin',
      referrerPolicy: 'same-origin',
      signal: options.signal,
    });

    // 202 Accepted: the atlas is warming off the web request. Poll until it lands.
    if (response.status === 202) {
      if (!notifiedBuilding) {
        notifiedBuilding = true;
        options.onBuilding?.();
      }
      if (Date.now() > deadline) {
        throw new Error('The atlas is still being built. Please try again in a moment.');
      }
      await delay(ATLAS_BUILD_POLL_MS, options.signal);
      continue;
    }

    if (!response.ok) {
      throw new Error(`Atlas request failed (${response.status}): ${response.statusText}`);
    }

    return response.json() as Promise<AtlasData>;
  }
}

export async function getDocumentIntelligence(
  documentId: number,
  municipalityId: number
): Promise<DocumentIntelligence> {
  const params = new URLSearchParams({ municipality_id: String(municipalityId) });
  return getJson<DocumentIntelligence>(
    `/ops/knowledge-atlas/documents/${documentId}?${params.toString()}`
  );
}

export async function searchAtlas(
  municipalityId: number,
  q: string
): Promise<AtlasSearchResults> {
  const params = new URLSearchParams({ municipality_id: String(municipalityId), q });
  return getJson<AtlasSearchResults>(`/ops/knowledge-atlas/search?${params.toString()}`);
}
